/* eslint-disable max-lines */
import React from 'react';
import { Col, FormGroup, Input, Label, Row, Card, CardBody, CardHeader } from 'reactstrap';

interface WorksheetQuestion {
  questionText: string;
  fieldName: string;
}

interface NC4StepTwoProps {
  formData: {
    single_itemizedDeductionsExceed: 'yes' | 'no' | '';
    single_childDeductionExceed: 'yes' | 'no' | '';
    single_federalAdjustments: 'yes' | 'no' | '';
    single_claimTaxCredits: 'yes' | 'no' | '';
    joint_itemizedDeductionsExceed: 'yes' | 'no' | '';
    joint_childDeductionExceed: 'yes' | 'no' | '';
    joint_federalAdjustments: 'yes' | 'no' | '';
    joint_claimTaxCredits: 'yes' | 'no' | '';
    joint_spouseBenefits: 'yes' | 'no' | '';
    separate_itemizedDeductionsExceed: 'yes' | 'no' | '';
    separate_childDeductionExceed: 'yes' | 'no' | '';
    separate_federalAdjustments: 'yes' | 'no' | '';
    separate_claimTaxCredits: 'yes' | 'no' | '';
    hoh_itemizedDeductionsExceed: 'yes' | 'no' | '';
    hoh_childDeductionExceed: 'yes' | 'no' | '';
    hoh_federalAdjustments: 'yes' | 'no' | '';
    hoh_claimTaxCredits: 'yes' | 'no' | '';
    filingStatus: 'single_married_separately' | 'head_of_household' | 'married_jointly_surviving_spouse' | '';
  };
  handleRadioChange: (name: string, value: 'yes' | 'no' | '') => void;
}

const WorksheetQuestionRow: React.FC<{
  questionNumber: number;
  questionText: string;
  fieldName: string;
  value: 'yes' | 'no' | '';
  onChange: (fieldName: string, value: 'yes' | 'no' | '') => void;
}> = ({ questionNumber, questionText, fieldName, value, onChange }) => {
  return (
    <Row className="mb-2 align-items-center">
      <Col md="9" xs="8">
        <Label className="mb-0" style={{ fontSize: '0.9rem' }}>
          {questionNumber}. {questionText}
        </Label>
      </Col>
      <Col md="3" xs="4" className="text-right">
        <FormGroup check inline className="mb-0 mr-2">
          <Input
            type="radio"
            name={fieldName}
            id={`${fieldName}_yes`}
            value="yes"
            checked={value === 'yes'}
            onChange={() => onChange(fieldName, 'yes')}
            required
          />
          <Label check htmlFor={`${fieldName}_yes`} className="mr-2" style={{ fontSize: '0.85rem' }}>
            Yes
          </Label>
        </FormGroup>
        <FormGroup check inline className="mb-0">
          <Input
            type="radio"
            name={fieldName}
            id={`${fieldName}_no`}
            value="no"
            checked={value === 'no'}
            onChange={() => onChange(fieldName, 'no')}
          />
          <Label check htmlFor={`${fieldName}_no`} style={{ fontSize: '0.85rem' }}>
            No
          </Label>
        </FormGroup>
      </Col>
    </Row>
  );
};

const NC4StepTwo: React.FC<NC4StepTwoProps> = ({ formData, handleRadioChange }) => {
  const singleQuestions: WorksheetQuestion[] = [
    {
      questionText: 'Will your N.C. itemized deductions from Page 3, Schedule 1 exceed $15,249?',
      fieldName: 'single_itemizedDeductionsExceed',
    },
    {
      questionText: 'Will your N.C. child deduction amount from Page 3, Schedule 2 exceed $2,499?',
      fieldName: 'single_childDeductionExceed',
    },
    {
      questionText: 'Will you have federal adjustments or N.C. deductions from income?',
      fieldName: 'single_federalAdjustments',
    },
    {
      questionText: 'Will you be able to claim any N.C. tax credits or tax credit carryovers?',
      fieldName: 'single_claimTaxCredits',
    },
  ];

  const marriedFilingJointlyQuestions: WorksheetQuestion[] = [
    {
      questionText: 'Will your N.C. itemized deductions from Page 3, Schedule 1 exceed $27,999?',
      fieldName: 'joint_itemizedDeductionsExceed',
    },
    {
      questionText: 'Will your N.C. child deduction amount from Page 3, Schedule 2 exceed $2,499?',
      fieldName: 'joint_childDeductionExceed',
    },
    {
      questionText: 'Will you have federal adjustments or N.C. deductions from income?',
      fieldName: 'joint_federalAdjustments',
    },
    {
      questionText: 'Will you be able to claim any N.C. tax credits or tax credit carryovers?',
      fieldName: 'joint_claimTaxCredits',
    },
    {
      questionText:
        'Will your spouse receive combined wages and taxable retirement benefits of less than $10,250 or only retirement benefits not subject to N.C. income tax?',
      fieldName: 'joint_spouseBenefits',
    },
  ];

  const marriedFilingSeparatelyQuestions: WorksheetQuestion[] = [
    {
      questionText: 'Will your portion of N.C. itemized deductions from Page 3, Schedule 1 exceed $15,249?',
      fieldName: 'separate_itemizedDeductionsExceed',
    },
    {
      questionText: 'Will your N.C. child deduction amount from Page 3, Schedule 2 exceed $2,499?',
      fieldName: 'separate_childDeductionExceed',
    },
    {
      questionText: 'Will you have federal adjustments or N.C. deductions from income?',
      fieldName: 'separate_federalAdjustments',
    },
    {
      questionText: 'Will you be able to claim any N.C. tax credits or tax credit carryovers?',
      fieldName: 'separate_claimTaxCredits',
    },
  ];

  const headOfHouseholdQuestions: WorksheetQuestion[] = [
    {
      questionText: 'Will your N.C. itemized deductions from Page 3, Schedule 1 exceed $21,624?',
      fieldName: 'hoh_itemizedDeductionsExceed',
    },
    {
      questionText: 'Will your N.C. child deduction amount from Page 3, Schedule 2 exceed $2,499?',
      fieldName: 'hoh_childDeductionExceed',
    },
    {
      questionText: 'Will you have federal adjustments or N.C. deductions from income?',
      fieldName: 'hoh_federalAdjustments',
    },
    {
      questionText: 'Will you be able to claim any N.C. tax credits or tax credit carryovers?',
      fieldName: 'hoh_claimTaxCredits',
    },
  ];

  const renderSection = (title: string, questions: WorksheetQuestion[]) => (
    <Card className="mb-4">
      <CardHeader tag="h6" className="bg-light p-2">
        {title}
      </CardHeader>
      <CardBody className="p-3">
        {questions.map((q, index) => (
          <WorksheetQuestionRow
            key={q.fieldName}
            questionNumber={index + 1}
            questionText={q.questionText}
            fieldName={q.fieldName}
            value={formData[q.fieldName as keyof typeof formData] as 'yes' | 'no' | ''}
            onChange={handleRadioChange}
          />
        ))}
      </CardBody>
    </Card>
  );

  return (
    <>
      <div className="text-center mb-4">
        <h4 className="mb-0" style={{ fontWeight: 'bold' }}>
          NC-4 Allowance Worksheet
        </h4>
        <p className="mb-0" style={{ fontSize: '1.1rem', fontWeight: 500 }}>
          Part I
        </p>
      </div>
      <p className="mb-3" style={{ fontSize: '0.9rem' }}>
        Answer all of the following questions for your filing status.
      </p>

      {(formData.filingStatus === 'single_married_separately' || !formData.filingStatus) &&
        renderSection('Single', singleQuestions)}

      {(formData.filingStatus === 'married_jointly_surviving_spouse' || !formData.filingStatus) &&
        renderSection('Married Filing Jointly', marriedFilingJointlyQuestions)}

      {(formData.filingStatus === 'single_married_separately' || !formData.filingStatus) &&
        renderSection('Married Filing Separately', marriedFilingSeparatelyQuestions)}

      {(formData.filingStatus === 'head_of_household' || !formData.filingStatus) &&
        renderSection('Head of Household', headOfHouseholdQuestions)}
    </>
  );
};

export default NC4StepTwo;
